import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";

import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";

import translate from "@insite/client-framework/Translate";
import {
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";

import Zone from "@insite/client-framework/Components/Zone";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import Hidden from "@insite/mobius/Hidden";
import { MappedLink } from "@insite/mobius/Menu";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GlobalProductMenu from "@MasterBluePrint/Components/GlobalProductMenu";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface HomePageStyles {
    page?: MobiusPageProps;
    productMenuWrapper?: InjectableCss;
    otherMenuWrapper?: InjectableCss;
    homepageWrapper?: InjectableCss;
}

export const homePageStyles: HomePageStyles = {
    page: {
        css: css`
            padding: .9375rem 0 0!important;
            @media (max-width: 767px) {
                padding: .9375rem !important;
            }
        `,
    },
    productMenuWrapper: {
        css: css`
            width: 25%;
            background: ${themeColors.colorWhite};
            position: relative;
            z-index: 1;
            max-width: 17.0625rem;
            min-width: 17.0625rem;
        `,
    },
    otherMenuWrapper: {
        css: css`
            width: 100%;
            overflow: hidden;
            align-items: start !important;
            position: relative; 

            h1[data-test-selector="PageTitle"] {
                margin-left: 1.4rem;
            }
            > div:first-child > div > div > div:first-child {
                padding:2rem 2.5rem 1.5rem;
                @media (max-width: 575px) {
                    padding:.3rem;
                }
            }
            > div:last-child {
                &:after {
                    content: "";
                    display: block;
                    left: 0;
                    top: 0;
                    width: 0;
                    height: 0;
                    position: absolute;
                    background: ${themeColors.colorBackgroundContrast};
                    opacity: .61;
                }
            }
            @media (min-width: 768px) and (max-width: 991px) {
                > div:first-child > div{
                    max-width: 100%;
                    padding: 0 1rem;
                }
            }
            @media (min-width: 576px) and (max-width: 767px) {
                > div:first-child > div{
                    max-width: 100%;
                    padding: 0 1rem;
                }

                > div:first-child > div > div > div > div > div {
                    max-width: 100%;
                }
            }
            @media (min-width: 768px) and (max-width: 991px){
                > div:first-child > div > div > div > div > div {
                    max-width: 100%;
                }
            }
        `,
    },
    homepageWrapper: {
        css: css`
            width: 100%;
            display: flex;
            
        `,
    },
};
const styles = homePageStyles;
const mapStateToProps = (state: ApplicationState) => ({
    mappedMenuLink: setupCategoryLink(state, emptyGuid, {}, 2, ""),
    homeUrl: getPageLinkByPageType(state, "HomePage")?.url,
});

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);
    if (
        (depthLoaded < maxDepth || !subCategoryIds) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: "",
                title: overrideTitle || translate("FIND YOUR PRODUCT"),
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
            };
        }
        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink | undefined,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink!.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink!.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};

type Props = PageProps & ReturnType<typeof mapStateToProps>;

const HomePage = ({ id, mappedMenuLink, homeUrl }: Props) => (
    <Page data-test-selector="homePage" {...styles.page}>
        <StyledWrapper {...styles.homepageWrapper}>
            <Hidden below="lg" {...styles.productMenuWrapper}>
                <StyledWrapper>
                    <GlobalProductMenu megaMenuLinks={mappedMenuLink} isHomePage={true}></GlobalProductMenu>
                </StyledWrapper>
            </Hidden>
            <StyledWrapper {...styles.otherMenuWrapper}>
                <Zone contentId={id} zoneName="Content" requireRows />
            </StyledWrapper>
            <Modals />
        </StyledWrapper>
    </Page>
);

const pageModule: PageModule = {
    component: connect(mapStateToProps)(HomePage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [MetaKeywords, MetaDescription, OpenGraphTitle, OpenGraphUrl, OpenGraphImage],
    },
};

export default pageModule;

export const HomePageContext = "HomePage";
